/**
 * Host Geolocation Utility
 *
 * Provides geolocation services for host matchmaking with:
 * - Parallel IP request racing (first response wins)
 * - 12-24 hour localStorage caching (hosts don't move)
 * - Automatic retry with exponential backoff on failure
 * - L3 geohash precision (3 chars, ~97mi cells)
 *
 * IP Geolocation Strategy: Parallel Request Racing
 * - Queries ALL services simultaneously
 * - Returns first successful response (minimum latency)
 * - Built-in redundancy (works as long as ANY service responds)
 *
 * Services:
 * 1. ipapi.co (free 1000/day, HTTPS)
 * 2. ip-api.com (free unlimited, HTTP)
 * 3. geojs.io (sponsored by DigitalOcean, unlimited, HTTPS)
 * 4. ipinfo.io (free 50k/month, HTTPS)
 * 5. ipwhois.pro (PAID, we pay for this)
 * 6. freegeoip.app (free, HTTPS)
 */
import * as geohash from 'ngeohash';

export interface GeolocationResult {
  hash: string; // L3 geohash (3 chars)
  coords: {
    lat: number;
    lng: number;
  };
  timestamp: number;
  method: 'ip';
  service?: string; // Which IP service was used (if applicable)
}

// Cache duration: 12 hours for hosts (hosts don't move much)
const CACHE_DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours
const CACHE_KEY = 'playcast_host_geolocation';

// Retry configuration
const MAX_RETRIES = 5;
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000]; // 1s, 2s, 4s, 8s, 16s

// In-memory cache (fast path)
let cachedGeohash: GeolocationResult | null = null;

/**
 * Get host geolocation with persistent caching and retry logic
 *
 * @returns Geohash (L3), coordinates, and metadata
 * @throws Error if all geolocation methods fail after retries
 */
export async function getHostGeohash(): Promise<GeolocationResult> {
  // Check in-memory cache first (fastest)
  if (cachedGeohash && (Date.now() - cachedGeohash.timestamp) < CACHE_DURATION_MS) {
    console.log('[Host Geolocation] Using in-memory cache:', cachedGeohash.hash);
    return cachedGeohash;
  }

  // Check localStorage cache (survives page reloads)
  const localStorageResult = getLocalStorageCache();
  if (localStorageResult) {
    cachedGeohash = localStorageResult;
    console.log('[Host Geolocation] Using localStorage cache:', localStorageResult.hash);
    return localStorageResult;
  }

  // Fetch fresh geolocation with retry logic
  return await getGeolocationWithRetries();
}

/**
 * Get geolocation with automatic retry on failure
 */
async function getGeolocationWithRetries(): Promise<GeolocationResult> {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      console.log(`[Host Geolocation] Attempt ${attempt + 1}/${MAX_RETRIES}`);

      const result = await getIPGeolocation();
      saveToCache(result);
      return result;
    } catch (err) {
      const isLastAttempt = attempt === MAX_RETRIES - 1;

      if (isLastAttempt) {
        console.error('[Host Geolocation] All attempts failed:', err);
        throw new Error('Cannot determine host location for matchmaking after retries');
      }

      const delay = RETRY_DELAYS_MS[attempt];
      console.warn(`[Host Geolocation] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`, err);
      await sleep(delay);
    }
  }

  throw new Error('Cannot determine host location for matchmaking');
}

/**
 * Save geolocation result to both in-memory and localStorage cache
 */
function saveToCache(result: GeolocationResult): void {
  cachedGeohash = result;

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(result));
    console.log('[Host Geolocation] Saved to cache:', result.hash);
  } catch (err) {
    console.warn('[Host Geolocation] Failed to save to localStorage:', err);
  }
}

/**
 * Get cached geolocation from localStorage (if valid)
 */
function getLocalStorageCache(): GeolocationResult | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const result = JSON.parse(cached) as GeolocationResult;

    // Check if cache is still valid
    if ((Date.now() - result.timestamp) >= CACHE_DURATION_MS) {
      console.log('[Host Geolocation] localStorage cache expired');
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return result;
  } catch (err) {
    console.warn('[Host Geolocation] Failed to read from localStorage:', err);
    return null;
  }
}

/**
 * IP Geolocation Services
 *
 * All services are queried in parallel - first successful response wins.
 * This provides both speed (minimum latency) and redundancy (works if ANY service responds).
 *
 * Note: Some services use HTTP (not HTTPS) on free tier, but acceptable since
 * geolocation data is not sensitive and we're getting approximate location only.
 */
const IP_TIMEOUT_MS = 5000; // 5 seconds per service
const IP_SERVICES = [
  {
    name: 'ipapi.co', // free, 30k requests.
    fetch: async () => {
      const response = await fetchWithTimeout('https://ipapi.co/json/', IP_TIMEOUT_MS);
      const data = await response.json();
      return { lat: data.latitude, lng: data.longitude, city: data.city, country: data.country_name };
    },
  },
  // {
  //   name: 'ip-api.com', // PAID, free for non-commercial
  //   fetch: async () => {
  //     const response = await fetchWithTimeout('http://ip-api.com/json/', IP_TIMEOUT_MS);
  //     const data = await response.json();
  //     if (data.status !== 'success') throw new Error(data.message || 'API returned failure status');
  //     return { lat: data.lat, lng: data.lon, city: data.city, country: data.country };
  //   },
  // },
  {
    name: 'geojs.io', // free commercial use
    fetch: async () => {
      const response = await fetchWithTimeout('https://get.geojs.io/v1/ip/geo.json', IP_TIMEOUT_MS);
      const data = await response.json();
      return { lat: parseFloat(data.latitude), lng: parseFloat(data.longitude), city: data.region, country: data.country };
    },
  },
  {
    name: 'ipinfo.io', // free commercial use
    fetch: async () => {
      const response = await fetchWithTimeout('https://ipinfo.io/json', IP_TIMEOUT_MS);
      const data = await response.json();
      const [lat, lng] = data.loc.split(',').map(parseFloat);
      return { lat, lng, city: data.city, country: data.country };
    },
  },
  {
    name: 'ipwhois.pro',
    fetch: async () => {
      const response = await fetchWithTimeout('https://ipwhois.pro/?key=6zndhBYkCbacD7rx', IP_TIMEOUT_MS);
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'API returned failure status');
      return { lat: data.latitude, lng: data.longitude, city: data.city, country: data.country };
    },
  },
  // {
  //   name: 'freegeoip.app', // free for non-commercial. ipbase.com
  //   fetch: async () => {
  //     const response = await fetchWithTimeout('https://freegeoip.app/json/', IP_TIMEOUT_MS);
  //     const data = await response.json();
  //     return { lat: data.latitude, lng: data.longitude, city: data.city, country: data.country_name };
  //   },
  // },
];

/**
 * Fetch with manual timeout (more reliable than AbortSignal.timeout in Electron)
 */
async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get geolocation using IP-based lookup with parallel request racing
 *
 * Queries ALL services simultaneously and returns the first successful response.
 * This provides:
 * - Speed: Minimum latency across all services (not sum of sequential timeouts)
 * - Redundancy: Works as long as ANY service responds successfully
 * - Resilience: Automatic failover without extra latency
 */
async function getIPGeolocation(): Promise<GeolocationResult> {
  console.log('[Host Geolocation] Racing IP services:', IP_SERVICES.map(s => s.name).join(', '));

  // Create promises for all services
  const racePromises = IP_SERVICES.map(async (service) => {
    try {
      const startTime = Date.now();
      const data = await service.fetch();

      if (!data.lat || !data.lng) {
        throw new Error('No coordinates in response');
      }

      const duration = Date.now() - startTime;
      const coords = { lat: data.lat, lng: data.lng };
      const hash = geohash.encode(coords.lat, coords.lng, 3); // L3 precision (3 chars, ~97mi)

      console.log(`[Host Geolocation] ✅ ${service.name} responded in ${duration}ms:`, {
        hash,
        coords,
        city: data.city,
        country: data.country,
      });

      return {
        hash,
        coords,
        timestamp: Date.now(),
        method: 'ip' as const,
        service: service.name,
      };
    } catch (err) {
      console.warn(`[Host Geolocation] ❌ ${service.name} failed:`, err);
      throw err; // Re-throw to let Promise.any handle it
    }
  });

  // Return first successful response, or throw if all fail
  try {
    const result = await Promise.any(racePromises);
    console.log(`[Host Geolocation] Winner: ${result.service}`);
    return result;
  } catch (aggregateError) {
    // All services failed
    console.error('[Host Geolocation] All IP services failed');
    throw new Error('All IP geolocation services failed');
  }
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Clear cached geolocation (forces refresh on next call)
 */
export function clearGeolocationCache(): void {
  cachedGeohash = null;
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log('[Host Geolocation] Cache cleared');
  } catch (err) {
    console.warn('[Host Geolocation] Failed to clear localStorage:', err);
  }
}

/**
 * Get cached geolocation (if available and valid)
 * Returns null if no cache or cache is stale
 */
export function getCachedGeohash(): GeolocationResult | null {
  // Check in-memory first
  if (cachedGeohash && (Date.now() - cachedGeohash.timestamp) < CACHE_DURATION_MS) {
    return cachedGeohash;
  }

  // Check localStorage
  return getLocalStorageCache();
}
